import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { CurrentHolding } from "../hooks/useCurrentPortfolio";
import HoldingDetailsModal from "./HoldingDetailsModal";
import { CompanyIdentity, companyCellSx } from "../../../shared/CompanyIdentity";
import { getFlatPortfolioColors, portfolioTypography } from "../../../../utils/getFlatPortfolioColors";

type SortColumn =
  | "company"
  | "positionValue"
  | "shares"
  | "totalReturn"
  | "dayChange";
type SortDirection = "asc" | "desc";

interface PortfolioTableViewProps {
  holdings: CurrentHolding[];
  isRefetching?: boolean;
}

const PortfolioTableView: React.FC<PortfolioTableViewProps> = ({
  holdings,
  isRefetching = false,
}) => {
  const [selectedHolding, setSelectedHolding] = useState<CurrentHolding | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>("positionValue");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const theme = useTheme();
  const colors = getFlatPortfolioColors(theme);
  const currencyCode = "USD";

  // Sorting function
  const getSortValue = (
    holding: CurrentHolding,
    column: SortColumn
  ): number | string => {
    switch (column) {
      case "company":
        return holding.name.toLowerCase();
      case "positionValue":
        return holding.currentValue;
      case "shares":
        return holding.shares;
      case "totalReturn":
        return holding.gainLoss;
      case "dayChange":
        return holding.dayChange;
      default:
        return 0;
    }
  };

  // Filter out zero-share holdings and apply sorting
  const sortedHoldings = useMemo(() => {
    if (!holdings || !Array.isArray(holdings) || holdings.length === 0) {
      return [];
    }

    const filtered = [...holdings].filter(
      (holding) => holding.shares > 0.000001
    );

    return filtered.sort((a, b) => {
      const aValue = getSortValue(a, sortColumn);
      const bValue = getSortValue(b, sortColumn);

      let comparison = 0;
      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = (aValue as number) - (bValue as number);
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [holdings, sortColumn, sortDirection]);

  // Handle undefined or empty holdings
  if (!holdings || !Array.isArray(holdings) || holdings.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          px: 4,
          backgroundColor: colors.background.secondary,
          border: `1px solid ${colors.border.primary}`,
          borderRadius: 0,
        }}
      >
        <Typography sx={{ 
          ...portfolioTypography.title,
          color: colors.text.secondary,
          mb: 2 
        }}>
          No Holdings Found
        </Typography>
        <Typography sx={{ 
          ...portfolioTypography.body,
          color: colors.text.muted 
        }}>
          Upload your trading history to see your portfolio holdings here.
        </Typography>
      </Box>
    );
  }

  // Handle column header click
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Render sort icon
  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === "asc" ? (
      <KeyboardArrowUp sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <KeyboardArrowDown sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  const formatCurrency = (amount: number) => {
    const locale =
      currencyCode === "USD"
        ? "en-US"
        : currencyCode === "EUR"
        ? "en-EU"
        : "en-GB";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatShares = (shares: number) => {
    return new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    }).format(shares);
  };

  const formatPercentage = (percent: number | undefined | null) => {
    if (percent === undefined || percent === null || isNaN(percent)) {
      return "0.00%";
    }
    const sign = percent >= 0 ? "+" : "";
    return `${sign}${percent.toFixed(2)}%`;
  };

  const handleRowClick = (holding: CurrentHolding) => {
    setSelectedHolding(holding);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedHolding(null);
  };

  // Get functional color based on value (gains = green, losses = red)
  const getValueColor = (value: number | null | undefined) => {
    if (value === null || value === undefined) return colors.text.primary;
    return value >= 0 ? colors.functional.success : colors.functional.error;
  };

  return (
    <Box>
      <TableContainer
        sx={{
          backgroundColor: colors.background.secondary,
          border: `1px solid ${colors.border.primary}`,
          borderRadius: 0, // Sharp corners - no rounding
          overflow: "hidden",
          opacity: isRefetching ? 0.8 : 1,
          transition: "opacity 0.3s ease",
          // NO shadows, NO elevation - pure flat design
        }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: colors.background.primary,
                borderBottom: `2px solid ${colors.border.strong}`,
              }}
            >
              <TableCell
                sx={{
                  width: 60,
                  py: 2,
                  pl: 3,
                  cursor: "pointer",
                  userSelect: "none",
                  borderBottom: "none",
                }}
                onClick={() => handleSort("company")}
                colSpan={2}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ "&:hover": { color: colors.functional.info } }}
                >
                  <Typography
                    sx={{
                      ...portfolioTypography.tableHeader,
                      color: colors.text.secondary,
                    }}
                  >
                    COMPANY
                  </Typography>
                  {renderSortIcon("company")}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{ py: 2, cursor: "pointer", userSelect: "none", borderBottom: "none" }}
                onClick={() => handleSort("positionValue")}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ "&:hover": { color: colors.functional.info } }}
                >
                  <Typography
                    sx={{
                      ...portfolioTypography.tableHeader,
                      color: colors.text.secondary,
                    }}
                  >
                    POSITION VALUE
                  </Typography>
                  {renderSortIcon("positionValue")}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{ py: 2, cursor: "pointer", userSelect: "none", borderBottom: "none" }}
                onClick={() => handleSort("shares")}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ "&:hover": { color: colors.functional.info } }}
                >
                  <Typography
                    sx={{
                      ...portfolioTypography.tableHeader,
                      color: colors.text.secondary,
                    }}
                  >
                    SHARES
                  </Typography>
                  {renderSortIcon("shares")}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{ py: 2, cursor: "pointer", userSelect: "none", borderBottom: "none" }}
                onClick={() => handleSort("totalReturn")}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ "&:hover": { color: colors.functional.info } }}
                >
                  <Typography
                    sx={{
                      ...portfolioTypography.tableHeader,
                      color: colors.text.secondary,
                    }}
                  >
                    TOTAL RETURN
                  </Typography>
                  {renderSortIcon("totalReturn")}
                </Box>
              </TableCell>
              <TableCell
                align="right"
                sx={{ py: 2, cursor: "pointer", userSelect: "none", borderBottom: "none" }}
                onClick={() => handleSort("dayChange")}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ "&:hover": { color: colors.functional.info } }}
                >
                  <Typography
                    sx={{
                      ...portfolioTypography.tableHeader,
                      color: colors.text.secondary,
                    }}
                  >
                    DAY CHANGE
                  </Typography>
                  {renderSortIcon("dayChange")}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedHoldings.map((holding) => (
              <TableRow
                key={holding.ticker}
                onClick={() => handleRowClick(holding)}
                sx={{
                  cursor: "pointer",
                  transition: "background-color 0.2s linear",
                  "&:hover": {
                    backgroundColor: colors.background.hover,
                    // NO shadows, NO transforms - pure flat design
                  },
                  "&:last-child td": {
                    borderBottom: 0,
                  },
                  "& .MuiTableCell-root": {
                    borderBottom: `1px solid ${colors.border.secondary}`,
                  },
                }}
              >
                {/* Logo & Company Name & Ticker */}
                <TableCell sx={{ ...companyCellSx, py: 2, pl: 3 }} colSpan={2}>
                  <CompanyIdentity
                    company={holding.name}
                    symbol={holding.symbol}
                    image={holding.logo}
                    linked={false}
                  />
                </TableCell>

                {/* Position Value */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      ...portfolioTypography.body,
                      fontWeight: 600,
                      color: colors.text.primary,
                    }}
                  >
                    {formatCurrency(holding.currentValue)}
                  </Typography>
                </TableCell>

                {/* Shares */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography
                    sx={{
                      ...portfolioTypography.body,
                      fontWeight: 500,
                      color: colors.text.primary,
                    }}
                  >
                    {formatShares(holding.shares)}
                  </Typography>
                </TableCell>

                {/* Total Return */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        ...portfolioTypography.body,
                        fontWeight: 600,
                        color: getValueColor(holding.gainLoss),
                        lineHeight: 1.2,
                      }}
                    >
                      {formatCurrency(holding.gainLoss)}
                    </Typography>
                    <Typography
                      sx={{
                        ...portfolioTypography.caption,
                        color: getValueColor(holding.gainLoss),
                        fontWeight: 500,
                      }}
                    >
                      {formatPercentage(holding.gainLossPercent)}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Day Change */}
                <TableCell align="right" sx={{ py: 2 }}>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        ...portfolioTypography.body,
                        fontWeight: 600,
                        color: getValueColor(holding.dayChange),
                        lineHeight: 1.2,
                      }}
                    >
                      {formatCurrency(holding.dayChange)}
                    </Typography>
                    <Typography
                      sx={{
                        ...portfolioTypography.caption,
                        color: getValueColor(holding.dayChange),
                        fontWeight: 500,
                      }}
                    >
                      {formatPercentage(holding.dayChangePercent)}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Modal */}
      {selectedHolding && (
        <HoldingDetailsModal
          open={modalOpen}
          onClose={handleModalClose}
          ticker={selectedHolding.ticker}
          companyName={selectedHolding.name}
          logo={selectedHolding.logo}
        />
      )}
    </Box>
  );
};

export default PortfolioTableView;
